import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  inject,
  Input,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { ModalService } from '@base/services/private/modal.service';
import { ModalHeaderComponent } from '@base/components/private/modals/modal-header/modal-header.component';
import { ModalBodyComponent } from '@base/components/private/modals/modal-body/modal-body.component';
import { Company, People, Product } from '@base/models/responses/populate-filter.res';
import { NgbAccordionDirective, NgbAccordionModule } from '@ng-bootstrap/ng-bootstrap';
import { ModalFooterComponent } from '@base/components/private/modals/modal-footer/modal-footer.component';
import { EventKeyword } from '@base/models/responses/recommendation-interests.res';
import { InterestSelected } from '@base/models/others/interest-selected';
import { TypeOfInterest } from '@base/enums/type-interest.enum';
import { CountInterestsSelectedPipe } from '@base/pipes/private/ui.pipe';
import { TranslatePipe } from '@base/pipes/private/translate.pipe';
import { FormsModule } from '@angular/forms';
import { ThemeService } from '@base/services/private/theme.service';
import { GlobalService } from '@base/services/private/global.service';

@Component({
  selector: 'app-modal-meet-filter',
  imports: [
    ModalHeaderComponent,
    ModalBodyComponent,
    NgbAccordionModule,
    ModalFooterComponent,
    CountInterestsSelectedPipe,
    FormsModule,
    forwardRef(() => TranslatePipe),
  ],
  templateUrl: './modal-meet-filter.component.html',
  styleUrl: './modal-meet-filter.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalMeetFilterComponent implements AfterViewInit, OnDestroy {
  private readonly modalSrv = inject(ModalService);
  private readonly themeSrv = inject(ThemeService);
  private readonly globalSrv = inject(GlobalService);

  @ViewChild('accordion') accordion: NgbAccordionDirective | undefined;

  @Input() detail?: Company | Product | People | EventKeyword | null = null;
  @Input() modalId = '';
  @Input() selectedFilter: InterestSelected[] = [];
  @Input() submitWord = 'SEARCH';

  searchTerm = '';
  localList = Array.isArray(this.detail?.value) ? [...(this.detail?.value ?? [])] : [];

  isBgDarkMode = this.themeSrv.isBgDarkMode;
  isColorDarkMode = this.themeSrv.isColorDarkMode;

  _selectedFilter: InterestSelected[] = [];

  ngAfterViewInit(): void {
    this.localList = Array.isArray(this.detail?.value) ? [...(this.detail?.value ?? [])] : [];
    this.changeArrowAccordionColor();
    this._selectedFilter = [...this.selectedFilter];
  }

  ngOnDestroy(): void {
    this.globalSrv.destroyModalRef(this.modalId);
  }

  /**
   * Gets the total selected filters.
   */
  get totalSelectedFilter(): InterestSelected[] {
    return this._selectedFilter;
  }

  /**
   * Changes the color of the accordion arrow based on the dark mode setting.
   * If dark mode is enabled, it inverts the color of the accordion button arrow.
   * @returns void
   */
  changeArrowAccordionColor(): void {
    if (this.isColorDarkMode) {
      const style = document.createElement('style');
      style.textContent = `
        .accordion-button::after {
          filter: invert(1);
        }
      `;
      document.head.appendChild(style);
    }
  }

  /**
   * Closes the modal with the given modalId.
   * @returns void
   */
  close(): void {
    this.modalSrv.close(this.modalId);
  }

  /**
   * Executes the search action by closing the modal and passing the selected filters.
   * @returns void
   */
  search(): void {
    this.selectedFilter = [...this._selectedFilter];
    this.modalSrv.close(this.modalId, { key: 'MODAL_FILTER', data: this.selectedFilter });
  }

  /**
   * Toggles the selection of a filter item.
   * Adds the filter to the selected list if not present, otherwise removes it.
   * @param id - The unique identifier of the filter (default: -1).
   * @param value - The value of the filter option.
   * @param subparent - The subparent category of the filter (optional).
   * @param parent - The parent category of the filter (optional).
   * @returns void
   */
  selectFilter(id = -1, value: string, subparent = '', parent = ''): void {
    const termType = this.detail?.term_type as TypeOfInterest | undefined;
    const idx = this._selectedFilter.findIndex(
      (item) => item.id === id && item.value === value && item.term_type === termType
    );
    if (idx > -1) {
      // found it
      this._selectedFilter.splice(idx, 1);
    } else {
      const data: InterestSelected = {
        type: 'attribute',
        id: id as number,
        value,
        parent,
        subparent,
        term_type: termType,
      };

      this._selectedFilter.push(data);
    }
  }

  /**
   * Checks if a filter item is selected based on id and value.
   * @param id - The unique identifier of the filter item.
   * @param value - The value of the filter option.
   * @returns boolean - True if the filter item is selected, otherwise false.
   */
  isSelected(id: number | undefined, value: string): boolean {
    const termType = this.detail?.term_type as TypeOfInterest | undefined;
    const findValue = this._selectedFilter.find(
      (item) => item.id === id && item.value === value && item.term_type === termType
    );
    if (!findValue) return false;

    return true;
  }

  /**
   * Clears all selected filters.
   */
  clear(): void {
    const termType = this.detail?.term_type as TypeOfInterest | undefined;
    if (this.detail?.term_id.toString() === '') {
      // meaning multi level
      const idsToRemove = this.detail?.value.map((item) => item.title_id);
      this._selectedFilter = this._selectedFilter.filter(
        (item) => item.term_type !== termType || !idsToRemove.includes(+item.id)
      );
      return;
    }
    this._selectedFilter = this._selectedFilter.filter(
      (item) => item.term_type !== termType || item.id !== this.detail?.term_id
    );
  }

  /**
   * The search term entered by the user.
   * TODO - move the logic to service worker for better performance
   * @returns void
   */
  onSearchChange(): void {
    const term = this.searchTerm.trim().toLowerCase();
    if (!this.detail?.value) {
      this._selectedFilter = [];
      return;
    }

    // if one level
    if (this.detail.term !== '') {
      this.localList = [...this.detail.value].filter((item) => item.title.toLowerCase().includes(term));
      return;
    }

    if (this.detail.term === '') {
      // two level
      this.localList = [...this.detail.value].map((parent) => {
        const value = parent.value.filter((item) => {
          return item.toLowerCase().includes(term);
        });

        if (value.length === 0) {
          // when no data, set to empty string to keep showing the parent or accordion
          return {
            ...parent,
            value: [''],
          };
        }

        return {
          ...parent,
          value,
        };
      });
    }

    this.autoExandAndCollapseAccordion();
  }

  /**
   * Automatically expands or collapses accordion panels based on the presence of values.
   * @returns void
   */
  autoExandAndCollapseAccordion(): void {
    this.localList.forEach((item, idx) => {
      if (item.value.length === 0 || item.value[0] === '') {
        this.accordion?.collapse('panel-' + (idx + 1));
      } else {
        this.accordion?.expand('panel-' + (idx + 1));
      }
    });
  }

  /**
   * Clears the search term and resets the local list to the original detail values.
   * @returns void
   */
  onClearSearch(): void {
    this.searchTerm = '';
    this.localList = Array.isArray(this.detail?.value) ? [...(this.detail?.value ?? [])] : [];
  }
}
